import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  computed,
  effect,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';

// Number of bars in the static waveform. Telegram's clients use a per-recording
// waveform decoded from the file metadata; we don't have that, so we draw a
// fixed pseudo-random pattern that *looks* like a voice note.
const WAVE_BAR_COUNT = 36;

// Generates a deterministic pseudo-random height between 0..1 for each bar.
// Same input → same wave, so a given message always renders the same way
// (and doesn't reshuffle on re-renders).
const buildWavePattern = (seed: string): number[] => {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const bars: number[] = [];
  for (let i = 0; i < WAVE_BAR_COUNT; i++) {
    h ^= h << 13;
    h ^= h >>> 17;
    h ^= h << 5;
    const norm = ((h >>> 0) % 1000) / 1000; // 0..1
    // Bias toward the middle so bars near edges aren't too tall — voice
    // notes typically taper at the start/end.
    const edgeBias = 1 - Math.abs(i - WAVE_BAR_COUNT / 2) / (WAVE_BAR_COUNT / 2);
    bars.push(0.18 + norm * 0.7 * (0.4 + edgeBias * 0.6));
  }
  return bars;
};

@Component({
  selector: 'app-audio-player',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="ap-root"
      [class.is-me]="variant === 'me'"
      [class.is-them]="variant !== 'me'"
      [class.is-playing]="playing()"
    >
      <button
        type="button"
        class="ap-play"
        (click)="toggle()"
        [disabled]="!ready()"
        [attr.aria-label]="playing() ? 'Pausar' : 'Reproducir'"
      >
        @if (playing()) {
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        } @else {
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 5v14l11-7L8 5z" />
          </svg>
        }
      </button>

      <div class="ap-body">
        <div
          class="ap-wave"
          (pointerdown)="onSeekStart($event)"
          (pointermove)="onSeekMove($event)"
          (pointerup)="onSeekEnd($event)"
          (pointercancel)="onSeekEnd($event)"
          #waveEl
          role="slider"
          [attr.aria-valuemin]="0"
          [attr.aria-valuemax]="100"
          [attr.aria-valuenow]="progressPct()"
          [attr.aria-label]="'Posición de reproducción'"
          tabindex="0"
          (keydown)="onWaveKeydown($event)"
        >
          @for (h of bars(); let i = $index; track i) {
            <span
              class="ap-bar"
              [class.played]="(i + 1) / bars().length * 100 <= progressPct()"
              [style.height.%]="h * 100"
            ></span>
          }
        </div>
        <div class="ap-meta">
          <span class="ap-time">{{ formatClock(currentTime() * 1000) }}</span>
          @if (rate() !== 1) {
            <button
              type="button"
              class="ap-rate"
              (click)="cycleRate(); $event.stopPropagation()"
              [attr.aria-label]="'Velocidad ' + rate() + 'x'"
            >
              {{ rate() }}x
            </button>
          } @else {
            <button
              type="button"
              class="ap-rate ap-rate--ghost"
              (click)="cycleRate(); $event.stopPropagation()"
              aria-label="Cambiar velocidad"
            >
              1x
            </button>
          }
          <span class="ap-time ap-time--total">{{ formatClock(totalMs()) }}</span>
          @if (messageTime || messageStatus) {
            <span class="ap-msg-meta">
              @if (messageTime) {
                <span class="ap-msg-time">{{ messageTime }}</span>
              }
              @if (messageStatus) {
                @switch (messageStatus) {
                  @case ('sending') {
                    <span class="ap-status" title="Enviando…">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                           aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 7v5l3 2" />
                      </svg>
                    </span>
                  }
                  @case ('sent') {
                    <span class="ap-status" title="Enviado">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                           aria-hidden="true">
                        <path d="m5 14 3.5 3.5L19 6.5" />
                      </svg>
                    </span>
                  }
                  @case ('delivered') {
                    <span class="ap-status" title="Entregado">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                           aria-hidden="true">
                        <path d="m5 14 3.5 3.5L19 6.5" />
                      </svg>
                    </span>
                  }
                  @case ('read') {
                    <span class="ap-status ap-status--read" title="Leído">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                           aria-hidden="true">
                        <path d="M2.5 13.833L6 17.5l1.024-1.073M16.5 6.5l-6.063 6.352m-2.937.981L11 17.5l10.5-11" />
                      </svg>
                    </span>
                  }
                  @case ('failed') {
                    <span class="ap-status ap-status--failed" title="Error">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                           aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 8v5" />
                        <circle cx="12" cy="16" r="0.6" fill="currentColor" />
                      </svg>
                    </span>
                    <button
                      type="button"
                      class="ap-retry"
                      (click)="retry.emit(); $event.stopPropagation()"
                      aria-label="Reintentar envío"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                           aria-hidden="true">
                        <path d="M3 12a9 9 0 1 0 3-6.7" />
                        <path d="M3 4v5h5" />
                      </svg>
                    </button>
                  }
                }
              }
            </span>
          }
        </div>
      </div>

      <audio
        #audioEl
        [src]="src"
        preload="metadata"
        (loadedmetadata)="onLoaded()"
        (timeupdate)="onTimeUpdate()"
        (ended)="onEnded()"
        (play)="onPlay()"
        (pause)="playing.set(false)"
      ></audio>
    </div>
  `,
  styles: [
    `
      :host { display: block; }

      .ap-root {
        --ap-accent: #2f7bf0;
        --ap-bar-idle: rgba(8, 37, 99, 0.22);
        --ap-bar-played: var(--ap-accent);
        --ap-bg: rgba(8, 37, 99, 0.04);

        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 8px 10px;
        background: var(--ap-bg);
        border-radius: 14px;
        font-family: inherit;
        color: var(--ap-accent);
        user-select: none;
        box-sizing: border-box;
      }

      .ap-root.is-me {
        --ap-accent: #ffffff;
        --ap-bar-idle: rgba(255, 255, 255, 0.45);
        --ap-bar-played: #ffffff;
        --ap-bg: linear-gradient(135deg, #2f7bf0 0%, #1e5fd4 100%);
        color: #ffffff;
      }

      .ap-play {
        width: 38px;
        height: 38px;
        border-radius: 50%;
        border: 0;
        background: var(--ap-accent);
        color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        flex: 0 0 auto;
        transition: transform 140ms ease, background 140ms ease;
        padding: 0;
      }

      .ap-root.is-me .ap-play {
        background: #ffffff;
        color: #1e5fd4;
      }

      .ap-play:hover:not(:disabled) {
        transform: scale(1.05);
      }
      .ap-play:active:not(:disabled) {
        transform: scale(0.96);
      }
      .ap-play:disabled {
        opacity: 0.55;
        cursor: progress;
      }

      .ap-play svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
        display: block;
      }

      .ap-body {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .ap-wave {
        height: 28px;
        display: flex;
        align-items: center;
        gap: 2px;
        cursor: pointer;
        touch-action: none;
        outline: none;
      }
      .ap-wave:focus-visible {
        box-shadow: 0 0 0 2px rgba(47, 123, 240, 0.35);
        border-radius: 4px;
      }

      .ap-bar {
        flex: 1;
        min-width: 2px;
        max-width: 4px;
        background: var(--ap-bar-idle);
        border-radius: 2px;
        transition: background 80ms linear;
      }
      .ap-bar.played {
        background: var(--ap-bar-played);
      }
      .ap-root.is-playing .ap-bar.played:last-of-type {
        animation: ap-bar-pulse 0.9s ease-in-out infinite;
      }

      @keyframes ap-bar-pulse {
        0%, 100% { opacity: 1; }
        50%      { opacity: 0.55; }
      }

      .ap-meta {
        display: flex;
        align-items: center;
        gap: 8px;
        font-variant-numeric: tabular-nums;
        font-size: 12px;
        line-height: 1;
      }

      .ap-time {
        opacity: 0.9;
      }
      .ap-time--total {
        margin-left: auto;
        opacity: 0.7;
      }

      // Message-level meta (timestamp + delivery status), rendered inside
      // the bubble next to the player's own clock so audio messages match
      // the look of text messages — clock + ✓ in the same corner of the
      // bubble. On the customer bubble (variant=me) both glyphs are white;
      // on agent bubbles they fall back to the accent color (matches the
      // bar-played tint).
      .ap-msg-meta {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        margin-left: 6px;
        opacity: 0.9;
        white-space: nowrap;
      }

      .ap-msg-time {
        font-variant-numeric: tabular-nums;
        font-size: 11px;
        line-height: 1;
      }

      .ap-status {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 14px;
        height: 14px;
      }

      .ap-status svg {
        width: 14px;
        height: 14px;
        display: block;
      }

      // Double-check glyph is wider — keep it from being squished.
      .ap-status--read svg {
        width: 16px;
        height: 14px;
      }

      .ap-status--failed { color: #ffd6d6; }
      .ap-root.is-them .ap-status--failed { color: var(--danger, #cc0000); }

      .ap-retry {
        border: 0;
        background: transparent;
        color: inherit;
        cursor: pointer;
        padding: 0;
        width: 16px;
        height: 16px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        opacity: 0.85;
        transition: transform 120ms ease, opacity 120ms ease;
      }
      .ap-retry:hover { opacity: 1; }
      .ap-retry:active { transform: scale(0.9); }
      .ap-retry svg { width: 14px; height: 14px; display: block; }

      .ap-rate {
        border: 1px solid currentColor;
        background: transparent;
        color: inherit;
        font-size: 11px;
        font-weight: 600;
        line-height: 1;
        padding: 2px 6px;
        border-radius: 999px;
        cursor: pointer;
        opacity: 0.85;
        transition: opacity 120ms ease, transform 120ms ease;
      }
      .ap-rate--ghost { opacity: 0.55; }
      .ap-rate:hover { opacity: 1; }
      .ap-rate:active { transform: scale(0.95); }

      audio {
        display: none;
      }
    `
  ]
})
export class AudioPlayerComponent implements OnDestroy {
  // Local blob URL or signed download URL — either works since the player
  // just hands the value off to a hidden <audio> element.
  @Input({ required: true }) src!: string;
  // Pre-known total duration (seconds). Used as the initial total so the
  // bubble has a clock even before loadedmetadata fires; once the audio
  // element knows the real duration we switch to that.
  @Input() durationSec?: number;
  // Color scheme. 'me' = customer message (filled blue bubble), 'them' =
  // admin message (light pill).
  @Input() variant: 'me' | 'them' = 'them';
  // Stable seed so the waveform doesn't reshuffle when the bubble re-renders.
  @Input() seed = 'voice';
  // Optional message-level metadata rendered inside the bubble (à la
  // WhatsApp / Telegram): the send time and the delivery status. We accept
  // a pre-formatted clock string so this component stays unaware of locale /
  // date library choices upstream. Pass `undefined` to hide.
  @Input() messageTime?: string;
  @Input() messageStatus?: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  // Emitted when the user taps the inline "retry" affordance shown on a
  // failed message. The host wires it to its own retry flow (re-sending the
  // original File ref kept on the optimistic message).
  @Output() retry = new EventEmitter<void>();
  // Fired the first time playback starts for this component instance. The
  // host uses it to notify the backend that the customer actually listened
  // to the message (vs. only seeing the bubble). Component-scoped — a
  // re-render counts as a new "first play"; cross-session dedupe is the
  // store's job.
  @Output() firstPlay = new EventEmitter<void>();
  private hasReportedFirstPlay = false;

  @ViewChild('audioEl', { static: true }) audioEl!: ElementRef<HTMLAudioElement>;
  @ViewChild('waveEl', { static: true }) waveEl!: ElementRef<HTMLElement>;

  playing = signal(false);
  ready = signal(false);
  currentTime = signal(0);
  audioDuration = signal(0);
  rate = signal(1);

  bars = computed(() => buildWavePattern(this.seed));

  // Total ms used by the meta clock. Prefers the audio-element duration
  // (authoritative) and falls back to the @Input duration so we still
  // render a sensible total before the audio metadata loads.
  totalMs = computed(() => {
    const fromEl = this.audioDuration();
    if (fromEl > 0 && Number.isFinite(fromEl)) return fromEl * 1000;
    if (this.durationSec && this.durationSec > 0) return this.durationSec * 1000;
    return 0;
  });

  progressPct = computed(() => {
    const total = this.totalMs() / 1000;
    if (total <= 0) return 0;
    return Math.min(100, (this.currentTime() / total) * 100);
  });

  private seeking = false;

  // Reapply playback rate whenever the user cycles it.
  private rateEffect = effect(() => {
    const r = this.rate();
    const el = this.audioEl?.nativeElement;
    if (el) el.playbackRate = r;
  });

  ngOnDestroy(): void {
    const el = this.audioEl?.nativeElement;
    if (el) {
      el.pause();
      el.src = '';
    }
  }

  toggle() {
    const el = this.audioEl.nativeElement;
    if (el.paused) {
      void el.play();
    } else {
      el.pause();
    }
  }

  onPlay() {
    this.playing.set(true);
    if (!this.hasReportedFirstPlay) {
      this.hasReportedFirstPlay = true;
      this.firstPlay.emit();
    }
  }

  onLoaded() {
    const el = this.audioEl.nativeElement;
    // Some recordings (webm without proper duration metadata) report
    // Infinity until you seek to the end. Force-resolve by seeking.
    if (!Number.isFinite(el.duration) || el.duration === 0) {
      el.currentTime = 1e9;
      // The seek will trigger another loadedmetadata + a timeupdate; once
      // duration is known we reset currentTime to 0.
      const handler = () => {
        if (Number.isFinite(el.duration) && el.duration > 0) {
          el.currentTime = 0;
          this.audioDuration.set(el.duration);
          this.ready.set(true);
          el.removeEventListener('timeupdate', handler);
        }
      };
      el.addEventListener('timeupdate', handler);
      return;
    }
    this.audioDuration.set(el.duration);
    this.ready.set(true);
  }

  onTimeUpdate() {
    if (this.seeking) return;
    this.currentTime.set(this.audioEl.nativeElement.currentTime);
  }

  onEnded() {
    this.playing.set(false);
    this.currentTime.set(0);
    this.audioEl.nativeElement.currentTime = 0;
  }

  // ── seek bar ────────────────────────────────────────────
  onSeekStart(ev: PointerEvent) {
    if (!this.ready()) return;
    this.seeking = true;
    (ev.currentTarget as Element).setPointerCapture?.(ev.pointerId);
    this.applySeekFromEvent(ev);
  }

  onSeekMove(ev: PointerEvent) {
    if (!this.seeking) return;
    this.applySeekFromEvent(ev);
  }

  onSeekEnd(ev: PointerEvent) {
    if (!this.seeking) return;
    this.applySeekFromEvent(ev);
    this.seeking = false;
    (ev.currentTarget as Element).releasePointerCapture?.(ev.pointerId);
  }

  private applySeekFromEvent(ev: PointerEvent) {
    const wave = this.waveEl.nativeElement;
    const rect = wave.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width));
    const total = this.totalMs() / 1000;
    const next = ratio * total;
    this.currentTime.set(next);
    const el = this.audioEl.nativeElement;
    if (Number.isFinite(el.duration) && el.duration > 0) {
      el.currentTime = next;
    }
  }

  onWaveKeydown(ev: KeyboardEvent) {
    const total = this.totalMs() / 1000;
    if (total <= 0) return;
    let next = this.currentTime();
    if (ev.key === 'ArrowLeft') next = Math.max(0, next - 5);
    else if (ev.key === 'ArrowRight') next = Math.min(total, next + 5);
    else if (ev.key === 'Home') next = 0;
    else if (ev.key === 'End') next = total;
    else if (ev.key === ' ' || ev.key === 'Enter') {
      ev.preventDefault();
      this.toggle();
      return;
    } else return;
    ev.preventDefault();
    this.currentTime.set(next);
    const el = this.audioEl.nativeElement;
    if (Number.isFinite(el.duration)) el.currentTime = next;
  }

  // ── playback rate ───────────────────────────────────────
  // Cycles through 1x → 1.5x → 2x → 1x. Matches Telegram's tap-to-cycle
  // affordance on long voice notes.
  cycleRate() {
    const next = this.rate() === 1 ? 1.5 : this.rate() === 1.5 ? 2 : 1;
    this.rate.set(next);
  }

  formatClock(ms: number): string {
    const total = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(total / 60).toString().padStart(2, '0');
    const s = (total % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }
}
